<script setup lang="ts">
import { ref } from 'vue';
import { useRoute } from '@/router/router';
import axios, { AxiosError } from 'axios';
import { isEmpty } from 'lodash';
import i18n from '@/localisation/i18n';
import { RfcRequest } from '@/utils/helpers/rest';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import { ApiError } from '@/utils/errors/api-error';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';

const route = useRoute();
const snackbar = useSnackbarManager();

const emit = defineEmits<{
  (e: 'close-dialog'): void;
}>();

const rfcText = ref('');
const svErrors = ref('');
const isProcessing = ref(false);

async function submitWish(req: RfcRequest): Promise<void> {
  try {
    await axios.post('/api/1/me/rfc', req);
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function onSubmit(): Promise<void> {
  if (isProcessing.value) {
    return;
  }
  isProcessing.value = true;
  svErrors.value = '';

  try {
    const text = rfcText.value.trim();
    if (isEmpty(text)) {
      svErrors.value = i18n.tc('makeWish.error');
      return;
    }

    await doSubmit(text);
  } finally {
    rfcText.value = '';
    isProcessing.value = false;
  }
}

async function doSubmit(text: string): Promise<void> {
  try {
    const req: RfcRequest = {
      rfcText: text,
      rfcPath: route.fullPath,
      rfcPage: route.name ? String(route.name) : '',
    };
    await submitWish(req);

    snackbar.confirm(i18n.tc('makeWish.thanks'));
    closeDialog();
  } catch (e) {
    svErrors.value = errorString(e);
  }
}

function closeDialog(): void {
  emit('close-dialog');
}

defineExpose({ onSubmit, closeDialog });
</script>

<template>
  <div>
    <VForm novalidate @submit.prevent>
      <VCardTitle>
        <span class="headline">{{ $t('makeWish.title') }}</span>
      </VCardTitle>
      <VCardText class="my-4">
        <VContainer>
          <VAlert v-if="svErrors" dense type="error">{{ svErrors }}</VAlert>
          <VAlert v-else dense type="info">{{ $t('makeWish.info') }}</VAlert>
        </VContainer>
        <VContainer>
          <VTextarea
            v-model="rfcText"
            auto-grow
            autofocus
            color="secondary"
            counter
            dense
            filled
            :hint="$t('makeWish.label')"
            outlined
            persistent-hint
          />
        </VContainer>
      </VCardText>

      <!-- card-actions slot -->
      <VCardActions>
        <slot name="cardActions" v-bind="{ onSubmit, closeDialog }">
          <VSpacer />
          <VBtn
            class="mx-6 px-6"
            color="primary"
            :disabled="isProcessing"
            :loading="isProcessing"
            type="submit"
            @click="onSubmit"
          >
            {{ $t('submitWish.button') }}
          </VBtn>
        </slot>
      </VCardActions>
    </VForm>
  </div>
</template>

<style lang="scss" scoped>
.is-formfield-group {
  margin: 20px 0 0 0;
}
</style>
